import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CouponEditsDto } from 'src/api/contracts';
import couponEditsApiService from 'src/api/services/couponEditsApiService';
import { AppState } from 'src/store/store';
import { CouponEdits, CouponEditsPeriod } from './types';

const initialState: CouponEdits = {
    status: 'initial',
    items: [],
    total: 0,
    period: null,
    pagination: {
        pageNumber: 1,
        pageSize: 100
    },
    selected: [],
    requestId: ''
};

export const loadCouponEditsCurrentPage = createAsyncThunk<CouponEditsDto, void, { state: AppState }>(
    'coupon-edits/load-curent-page',
    async (_, { getState }) => {
        const state = getState();
        const period = selectCouponEditsPeriod(state);
        if (!period || !period.dateStart || !period.dateEnd) {
            throw new Error('Не задан период');
        }
        const pagination = selectCouponEditsPagination(state);
        const resp = await couponEditsApiService.loadCouponEdits({
            filter: {
                departDateStart: period.dateStart.format('YYYY-MM-DD'),
                departDateEnd: period.dateEnd.format('YYYY-MM-DD')
            },
            pagination: {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize
            }
        });
        return resp;
    }
);

export const loadCouponEditsNextPage = createAsyncThunk<
    CouponEditsDto,
    { pageNumber: number; pageSize?: number },
    { state: AppState }
>('coupon-edits/load-next-page', async (nextPagination: { pageNumber: number; pageSize?: number }, { getState }) => {
    const state = getState();
    const period = selectCouponEditsPeriod(state);
    if (!period || !period.dateStart || !period.dateEnd) {
        throw new Error('Не задан период');
    }
    const pagination = selectCouponEditsPagination(state);
    const resp = await couponEditsApiService.loadCouponEdits({
        filter: {
            departDateStart: period.dateStart.format('YYYY-MM-DD'),
            departDateEnd: period.dateEnd.format('YYYY-MM-DD')
        },
        pagination: {
            pageNumber: nextPagination.pageNumber,
            pageSize: nextPagination.pageSize ?? pagination.pageSize
        }
    });
    return resp;
});

export const deleteCouponEdits = createAsyncThunk<void, void, { state: AppState }>(
    'coupon-edits/delete',
    async (_, { getState }) => {
        const selected = selectCouponEditsSelection(getState());
        await couponEditsApiService.deleteCouponEdits(selected);
    }
);
const slice = createSlice({
    name: 'coupon-edits',
    initialState,
    reducers: {
        periodChanged: (state, action: PayloadAction<CouponEditsPeriod>) => {
            state.period = action.payload;
            state.items = [];
            state.total = 0;
        },
        selectionChanged: (state, action: PayloadAction<number[]>) => {
            state.selected = action.payload;
        },
        reset: () => initialState
    },
    extraReducers(builder) {
        builder
            .addCase(loadCouponEditsCurrentPage.pending, (state, action) => {
                state.status = 'loading';
                state.requestId = action.meta.requestId;
                state.selected = [];
            })
            .addCase(loadCouponEditsCurrentPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                }
            })
            .addCase(loadCouponEditsCurrentPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                    state.total = 0;
                }
            })
            .addCase(loadCouponEditsNextPage.pending, (state, action) => {
                state.status = 'loading';
                state.pagination.pageNumber = action.meta.arg.pageNumber;
                state.selected = [];
                if (action.meta.arg.pageSize) {
                    state.pagination.pageSize = action.meta.arg.pageSize;
                }
                state.requestId = action.meta.requestId;
            })
            .addCase(loadCouponEditsNextPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                    state.pagination.pageSize = action.payload.pageSize;
                }
            })
            .addCase(loadCouponEditsNextPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                }
            })
            .addCase(deleteCouponEdits.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteCouponEdits.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.selected = [];
                }
            })
            .addCase(deleteCouponEdits.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                }
            });
    }
});

const selectCouponEdits = (state: AppState) => state.couponEdits;

export const selectCouponEditsStatus = (state: AppState) => selectCouponEdits(state).status;

export const selectCouponEditsIsLoading = (state: AppState) => selectCouponEditsStatus(state) === 'loading';

export const selectCouponEditsItems = (state: AppState) => selectCouponEdits(state).items;

export const selectCouponEditsTotal = (state: AppState) => selectCouponEdits(state).total;

export const selectCouponEditsPeriod = (state: AppState) => selectCouponEdits(state).period;

export const selectCouponEditsCanLoadData = createSelector(
    selectCouponEditsPeriod,
    (period) => period && period.dateStart && period.dateEnd
);

export const selectCouponEditsPagination = (state: AppState) => selectCouponEdits(state).pagination;

export const selectCouponEditsSelection = (state: AppState) => selectCouponEdits(state).selected;

export const { periodChanged, selectionChanged, reset } = slice.actions;

export default slice.reducer;
